/**
 * The basemap — the FIRST of the three imagery layers, and the only one that is always on.
 *
 * It answers "where am I", nothing else. No date, no field, no run, no login, no Earth
 * Engine and no call to our own backend: a provider composite the browser caches. The other
 * two layers — dated imagery over the viewport (hooks/useViewportDates.ts, map/preview.ts)
 * and the run's marks on the calendar (components/SatelliteCalendar.tsx) — sit on top of it
 * and may be absent without this one being affected. See docs/MAP_IMAGERY_RESEARCH.md §2.
 *
 * WHICH PROVIDER, AND WHY IT IS NOT ESRI ANY MORE. Both configs below are ported verbatim
 * from the main agricultural product (`frontend/src/components/ui/DrawFieldMap.tsx:19-29`),
 * which is the owner's decision of 2026-08-05: that map works, and its basemaps come with
 * it. Two consequences follow and neither is editorialised away here:
 *
 *   - the hybrid tile has the place names BAKED IN. There is no separate reference layer to
 *     switch off, so the «Подписи» toggle that used to hide the Esri overlays has no subject
 *     left and is gone. What replaced it is a switch between the two basemaps, which is a
 *     thing the user can actually see happen;
 *   - `maxNativeZoom: 20` is the source's number and it has NOT been measured over Russia.
 *     The Esri cap of 17 was measured (grey «Map data not yet available» placeholders at
 *     Murmansk and Arkhangelsk, 2026-08-05); this one is inherited unverified and is
 *     recorded as such rather than presented as a measurement.
 *
 * The attribution strings are the source's, unchanged, and they are rendered on the map by
 * `L.control.attribution` — a licence condition, not a courtesy.
 */

import * as L from "leaflet";

import type { Theme } from "../hooks/useTheme";
import { PANES } from "./panes";

export type BasemapId = "hybrid" | "street";

export interface TileConfig {
  url: string;
  subdomains: string[];
  maxNativeZoom: number;
  attribution: string;
}

/** Ported verbatim, endpoints and attribution alike. */
export const TILE_CONFIGS: Record<BasemapId, TileConfig> = {
  hybrid: {
    url: "https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
    maxNativeZoom: 20,
    attribution: "&copy; Google",
  },
  street: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    subdomains: ["a", "b", "c"],
    maxNativeZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
};

export const BASEMAP_IDS = Object.keys(TILE_CONFIGS) as BasemapId[];

/** Beyond `maxNativeZoom` Leaflet stretches the last real level instead of asking for one. */
export const MAP_MAX_ZOOM = 22;
export const MAP_MIN_ZOOM = 2;

export const basemapLayer = (id: BasemapId): L.TileLayer => {
  const config = TILE_CONFIGS[id];
  return L.tileLayer(config.url, {
    subdomains: config.subdomains,
    maxZoom: MAP_MAX_ZOOM,
    maxNativeZoom: config.maxNativeZoom,
    attribution: config.attribution,
    pane: PANES.basemap,
  });
};

/**
 * Tinting for an aerial composite, not for a street map.
 *
 * MapLibre did this with raster paint properties inside the style; Leaflet has none, so it
 * is a CSS filter on the two tile panes — a different physics (colour multiplication against
 * raster operations), so the numbers were re-picked rather than transliterated from
 * `raster-saturation: -0.18 / brightness-max: 0.86 / contrast: 0.05`.
 *
 * The filter goes on the PANES, not on the map container: the field outlines and the marks
 * live in other panes, and dimming them too was the original reason the tint was not a
 * filter. Both photographs — basemap and dated imagery — get the same curve, because the
 * person looking is doing exactly one thing: comparing the soil inside the contour with the
 * soil outside it, and two tone curves put a seam along the boundary.
 *
 * Deliberately gentle. The previous numbers were tuned against OSM's flat white paper; on
 * photography a hard desaturation turns every field the same grey, which is the one
 * distinction the operator is looking for.
 */
export const TILE_FILTER: Record<Theme, string> = {
  dark: "saturate(0.82) brightness(0.88) contrast(1.05)",
  light: "none",
};

/**
 * Vector colours of the map, per theme.
 *
 * The map is the hero of the screen, and it used to be painted with hardcoded dark-theme
 * hexes: on a light basemap the unselected outline (#f0e9df at 0.65 opacity) came out at
 * about 1.05:1 and the fields simply disappeared. Leaflet path options cannot read CSS
 * variables, so the palette is mirrored here — same values as styles/tokens.css.
 */
export interface MapPalette {
  selectedFill: string;
  fill: string;
  selectedLine: string;
  line: string;
  fillOpacity: number;
  lineOpacity: number;
}

/*
 * There used to be three more entries here — `point`, `pointOutline`, `midPoint` — for Terra
 * Draw's vertex handles, which took hex literals in JavaScript. Geoman's handles are DOM
 * nodes and read CSS variables directly, so they are styled from the tokens themselves
 * (`.marker-icon` in styles/layout.css) instead of from a second copy of them here.
 */

export const MAP_PALETTE: Record<Theme, MapPalette> = {
  dark: {
    selectedFill: "#e0a03c", // --state-bare
    fill: "#a2968a", // --ink-dim
    selectedLine: "#e0a03c",
    line: "#f0e9df", // --ink
    fillOpacity: 0.08,
    lineOpacity: 0.65,
  },
  light: {
    selectedFill: "#8a5a16",
    fill: "#5f574c", // --ink-dim, light
    selectedLine: "#8a5a16",
    line: "#221d18", // --ink, light
    fillOpacity: 0.12,
    lineOpacity: 0.85,
  },
};

/**
 * Lettering over a photograph, and the outline that makes it survive one.
 *
 * White type fails from two opposite directions and the numbers say so: the warm-bone glyph
 * on sunlit stubble is 1.53:1 (dead), its bistre halo on the same stubble 10.3:1 (carries);
 * over dark forest they swap (12.0:1 and 1.30:1). Exactly ONE half of the pair works at any
 * moment, so each half has to carry the letterform alone — hence a halo at least 12 % of the
 * cap height and never a hairline.
 *
 * The colours do NOT follow the theme. The same photograph lies under them in both: the
 * light theme changes the imagery's tone curve, not the map's background, and a dark label
 * would vanish over the forest that covers half the frame north of 55°. The one thing the
 * theme moves is the halo width, because the light exposure is the brighter one.
 */
export const MAP_LABEL_INK = "#f0e9df"; // warm bone, not pure white: white argues with cloud
export const MAP_LABEL_HALO = "#14110e"; // warm bistre, not black: black punches a hole in forest

export const MAP_LABEL_PAINT: Record<Theme, { haloWidth: number }> = {
  dark: { haloWidth: 1.5 },
  light: { haloWidth: 1.9 },
};

/**
 * The same rule applied to the line, and there it is a defect we shipped: an unselected
 * outline at #f0e9df / 0.65 opacity is the same 1.53:1 over stubble. A dark casing runs
 * under the outline so that the contour, like the type, wears its own halo.
 */
export const MAP_LINE_CASING = { color: "#14110e", opacity: 0.55, extraWidth: 2 } as const;

/** One place for both line layers, so the casing can never stop being wider than the line. */
export const FIELD_LINE_WIDTH = { selected: 3, plain: 1.25 } as const;

/** Russia, roughly centred: the product's region is 44-70° N, UTM 35-60+. */
export const INITIAL_VIEW = { center: [51.7, 40.5] as L.LatLngTuple, zoom: 5 };
